import { LoadingOutlined, CopyOutlined } from '@ant-design/icons'
import type { PriceBreakdownResponse } from '../../../../../../../api/useSupplierReconciliation/usePriceBreakdown'
import { GraviGrid, Horizontal, Texto, Vertical, GraviButton } from '@gravitate-js/excalibrr'
import { message } from 'antd'
import { useFormatter } from '@api/useFormatter'
import { formatters } from '@utils/intl/dateTimeFormatters'

import { createPriceBreakdownColumnDefs } from './priceBreakdownColumnDefs'
import { TitleLabel } from '@/components/_SiteWide/TitleLabel'
import { startCase } from 'lodash'

interface PriceBreakdownContentProps {
  isFetchingData?: boolean
  data?: PriceBreakdownResponse
}

export function PriceBreakdownContent({ isFetchingData, data }: PriceBreakdownContentProps) {
  const { formatCurrency } = useFormatter()
  const getColumnDefs = createPriceBreakdownColumnDefs()

  if (isFetchingData) {
    return (
      <Horizontal fullHeight horizontalCenter verticalCenter>
        <Texto category='h1'>
          <LoadingOutlined />
        </Texto>
      </Horizontal>
    )
  }

  return (
    <Vertical>
      <Horizontal className='p-3' border='all' borderRadius={4}>
        <Vertical gap='8px'>
          <Horizontal width='100%'>
            <TitleLabel flex={1} title='Product'>
              {data?.product_name}
            </TitleLabel>
            <TitleLabel flex={1} title='Price Type'>
              {startCase(data?.price_type ?? '')}
            </TitleLabel>
            <TitleLabel flex={0.75} title='Supplier'>
              {data?.supplier_name}
            </TitleLabel>
            <TitleLabel flex={1} title='Terminal'>
              {data?.terminal_name}
            </TitleLabel>
            <TitleLabel flex={0.5} title='Price' align='right'>
              {formatCurrency(data?.price || 0, true, 4)}
            </TitleLabel>
          </Horizontal>
          <Horizontal>
            <TitleLabel flex={1} title='Effective From'>
              {data?.effective_from
                ? formatters.DATE_TIME(data.effective_from, String(data?.effective_from_local))
                : ''}
            </TitleLabel>
            <TitleLabel flex={1} title='Effective To'>
              {data?.effective_to ? formatters.DATE_TIME(data.effective_to, String(data?.effective_to_local)) : ''}
            </TitleLabel>
            <TitleLabel flex={0.75} title='Lifting Method'>
              {data?.lifting_method ? startCase(data.lifting_method) : ''}
            </TitleLabel>
            <TitleLabel flex={1} title='Expires'>
              {data?.expiration ? formatters.DATE_TIME(data.expiration, String(data?.expiration_local)) : ''}
            </TitleLabel>
            <TitleLabel flex={0.5} title='Net/Gross Type' align='right'>
              {data?.net_or_gross_type ? startCase(data.net_or_gross_type) : ''}
            </TitleLabel>
          </Horizontal>
        </Vertical>
      </Horizontal>
      {data?.source === 'Pricing Engine' && (
        <>
          <Horizontal className='px-3 py-3' verticalCenter>
            <Texto appearance='hint' className='mr-4'>
              Formula Name:
            </Texto>
            <Texto category='h5'>{data?.formula_name}</Texto>
          </Horizontal>
          <Vertical flex='0' className='px-3 pb-3' gap={16} style={{ overflow: 'visible' }}>
            <Vertical flex='0' gap={8} style={{ overflow: 'visible' }}>
              <Horizontal verticalCenter justifyContent='space-between'>
                <Texto category='p2' appearance='hint'>
                  Formula Template:
                </Texto>
                <GraviButton
                  size='small'
                  type='text'
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(data?.formula_template || '')
                    message.success('Formula template copied to clipboard')
                  }}
                >
                  Copy to clipboard
                </GraviButton>
              </Horizontal>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: '#1e1e1e',
                  borderRadius: '6px',
                  fontFamily: 'Monaco, Consolas, monospace',
                  fontSize: '14px',
                  color: '#d4d4d4',
                  lineHeight: '1.5',
                }}
              >
                {data?.formula_template}
              </div>
            </Vertical>
            <Vertical flex='0' gap={8} style={{ overflow: 'visible' }}>
              <Horizontal verticalCenter justifyContent='space-between'>
                <Texto category='p2' appearance='hint'>
                  Calculated Formula:
                </Texto>
                <GraviButton
                  size='small'
                  type='text'
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(data?.formula || '')
                    message.success('Calculated formula copied to clipboard')
                  }}
                >
                  Copy to clipboard
                </GraviButton>
              </Horizontal>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: '#1e1e1e',
                  borderRadius: '6px',
                  fontFamily: 'Monaco, Consolas, monospace',
                  fontSize: '14px',
                  color: '#4ec9b0',
                  lineHeight: '1.5',
                  fontWeight: 500,
                }}
              >
                {data?.formula}
              </div>
            </Vertical>
          </Vertical>
          <Horizontal flex={7} fullHeight width='100%'>
            <Vertical>
              <GraviGrid
                controlBarProps={{
                  title: 'Variables',
                }}
                agPropOverrides={{
                  className: 'fullHeight',
                  getRowId: (params) => params.data?.name || Math.random().toString(),
                }}
                rowData={data?.variables}
                columnDefs={getColumnDefs}
              />
            </Vertical>
          </Horizontal>
        </>
      )}
    </Vertical>
  )
}
